import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import AuthService from '../../modules/Admin/services/AuthService';
import {
  ensureModuleAccessLoaded,
  isPathModuleAllowed,
  MODULE_ACCESS_STORAGE_KEY,
} from '../utils/moduleAccessStore';
import { ensureSubscriptionChecked } from '../utils/subscriptionAccessStore';

/** JWT may still carry legacy role id 3 (removed “Manager”). Treat like Admin for route checks only. */
function normalizeEffectiveRoleId(roleNum) {
  if (!Number.isFinite(roleNum)) return roleNum;
  if (roleNum === 3) return 2;
  return roleNum;
}

const ProtectedRoute = ({ children, allowedRoles }) => {
  const location = useLocation();
  const isAuthenticated = AuthService.isAuthenticated();
  const currentRole = AuthService.getLoggedInRoleId();
  const roleNum = parseInt(currentRole, 10);
  const effectiveRole = normalizeEffectiveRoleId(roleNum);

  const needsModuleBootstrap = effectiveRole !== 1 && !!AuthService.getLoggedInClientId();

  const [modsReady, setModsReady] = useState(
    () => !needsModuleBootstrap || !!sessionStorage.getItem(MODULE_ACCESS_STORAGE_KEY)
  );
  const [subReady, setSubReady] = useState(() => effectiveRole === 1);
  const [subActive, setSubActive] = useState(true);

  useEffect(() => {
    if (!needsModuleBootstrap) return undefined;
    let cancelled = false;
    ensureModuleAccessLoaded().then(() => {
      if (!cancelled) setModsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [needsModuleBootstrap]);

  useEffect(() => {
    if (effectiveRole === 1) return undefined;
    let cancelled = false;
    ensureSubscriptionChecked().then((active) => {
      if (!cancelled) {
        setSubActive(active);
        setSubReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveRole]);

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!allowedRoles.includes(effectiveRole)) {
    return <Navigate to="/unauthorized" />;
  }

  if (!modsReady || !subReady) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#8392ab', fontSize: '14px' }}>
        <i className="fas fa-circle-notch fa-spin me-2" />
        Loading workspace…
      </div>
    );
  }

  if (effectiveRole !== 1 && !subActive) {
    return <Navigate to="/subscription-inactive" replace />;
  }

  if (needsModuleBootstrap && !isPathModuleAllowed(location.pathname)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default ProtectedRoute;
